import { registerInsights } from './insights';
import { DEFAULT_LANGUAGE, type Language } from './languages';
import { isSupportedLength } from './lengths';

const loaded = new Map<string, Promise<void>>();

async function loadEnglish(length: number): Promise<void> {
  switch (length) {
    case 4: registerInsights((await import('./data/dictionary4')).DICTIONARY_4); return;
    case 6: registerInsights((await import('./data/dictionary6')).DICTIONARY_6); return;
    case 7: registerInsights((await import('./data/dictionary7')).DICTIONARY_7); return;
    default: return;
  }
}

async function loadDutch(length: number): Promise<void> {
  switch (length) {
    case 4: registerInsights((await import('./data/nl/dictionary4')).DICTIONARY_NL_4, 'nl'); return;
    case 5: registerInsights((await import('./data/nl/dictionary5')).DICTIONARY_NL_5, 'nl'); return;
    case 6: registerInsights((await import('./data/nl/dictionary6')).DICTIONARY_NL_6, 'nl'); return;
    case 7: registerInsights((await import('./data/nl/dictionary7')).DICTIONARY_NL_7, 'nl'); return;
    default: return;
  }
}

/**
 * Loads and registers the insight chunk for a language's length, so
 * `insightFor` can answer for that length's answers (spec §10.5). The
 * English 5-letter dictionary is already in the main bundle and resolves
 * immediately; every Dutch length is a chunk, including 5. An unplayable
 * length is a silent no-op rather than a rejection, because insights are a
 * nicety — a game must never fail to start over a missing definition.
 *
 * A failed chunk load rejects (and is not cached, so a retry retries).
 * Callers that only want the definition should `.catch(() => {})`.
 */
export function loadInsightsFor(
  length: number,
  language: Language = DEFAULT_LANGUAGE,
): Promise<void> {
  const key = `${language}:${length}`;
  const hit = loaded.get(key);
  if (hit !== undefined) return hit;
  if (!isSupportedLength(length)) return Promise.resolve();
  if (language === DEFAULT_LANGUAGE && length === 5) return Promise.resolve();

  const pending = language === 'nl' ? loadDutch(length) : loadEnglish(length);
  loaded.set(key, pending);
  void pending.catch(() => loaded.delete(key));
  return pending;
}
